import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Container, Flex } from '@qga/roo-ui/components';
import pluralize from 'pluralize';
import PropTypes from 'prop-types';

import useAirport from '@shared/v2/components/Airport/hooks/useAirport/useAirport';

import QantasFrequentFlyerBanner from '@/components/Campaigns/QantasFrequentFlyerBanner';
import ConditionsMarkdown from '@/components/ConditionsMarkdown';
import FAQs from '@/components/DestinationPage/components/FAQs/FAQs';
import Heading from '@/components/Heading';
import ImportantInformation from '@/components/ImportantInformation';
import Pagination from '@/components/Pagination';
import Text from '@/components/Text';
import ValueProposition from '@/components/ValueProposition';
import { POINTS_EARN_SLUG } from '@/constants';
import Campaign, { Tile } from '@/propTypes/Campaign';
import Destination from '@/propTypes/Destination';
import getProcuredOfferFlightBadgeCode from '@/utils/campaigns/getProcuredOfferFlightBadgeCode';
import getStandardPropertyFlightBadgeCode from '@/utils/campaigns/getStandardPropertyFlightBadgeCode';
import isCampaignHidePerPersonPrice from '@/utils/isCampaignHidePerPersonPrice';
import { Destinations } from '@/v2/components/ui/Destinations';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import { usePointsEarn } from '@/v2/hooks/featureFlags/pointsEarn/usePointsEarn';
import useViewItemListEvent from '@/v2/hooks/useViewItemListEvent';
import {
  flyingFromChangeEvent,
  travellingToChangeEvent,
} from '@/v2/utils/dataLayer/searchEvents/events';
import useCampaignData from '../../hooks/useCampaignData';
import DepartingFrom from '../DepartingFrom';
import Tiles from '../Tiles';
import TravellingTo from '../TravellingTo';
import Banner from './components/Banner';
import FilterBanner from './components/FilterBanner';
import PointAccordion from './components/PointAccordion';

const DealsPageContent = ({
  campaign,
  campaigns,
  destinations = [],
  tiles = [],
}) => {
  const paginationScrollTo = useRef();
  const shouldUseNewEvents = useGA4Events();

  const paginatedPropertiesToShow = 20;
  const [paginatedProperties, setPaginatedProperties] = useState();

  const campaignPage = campaign?.campaignLandingPage || {};

  const {
    originCode,
    setOriginCode,
    originCodes,
    destinationName,
    setDestinationName,
    destinationNames,
    filteredProperties,
    filteredDestinations,
  } = useCampaignData({ destinations, tiles });

  const { value: airportOriginCode, setValue: setStoredOriginCode } =
    useAirport({
      parameterName: 'originCode',
    });

  useEffect(() => {
    // Reset the originCode when the stored origin doesn't fly to the Campaign's location
    if (airportOriginCode && !originCodes.includes(airportOriginCode)) {
      setOriginCode(null);
      setStoredOriginCode(null);
    }
  }, [airportOriginCode, originCodes, setOriginCode, setStoredOriginCode]);

  const hidePerPersonPrice = isCampaignHidePerPersonPrice(campaign);

  const scrollIntoView = useCallback(() => {
    paginationScrollTo?.current?.scrollIntoView?.({
      block: 'nearest',
    });
  }, []);

  const handleSetOrigin = useCallback(
    (selectedValue) => {
      if (selectedValue === null) {
        setOriginCode(null);
        setStoredOriginCode(null);
      } else {
        setOriginCode(selectedValue);
        if (shouldUseNewEvents) {
          flyingFromChangeEvent(selectedValue);
        }
      }
      scrollIntoView();
    },
    [scrollIntoView, setOriginCode, setStoredOriginCode, shouldUseNewEvents],
  );

  const handleSetDestination = useCallback(
    (...args) => {
      if (shouldUseNewEvents) {
        travellingToChangeEvent(...args);
      }
      setDestinationName(...args);
      scrollIntoView();
    },
    [scrollIntoView, setDestinationName, shouldUseNewEvents],
  );

  useEffect(() => {
    setPaginatedProperties(
      filteredProperties.slice(0, paginatedPropertiesToShow),
    );
  }, [filteredProperties]);

  const updatePaginatedProperties = (_, value) => {
    const first = (value - 1) * paginatedPropertiesToShow;
    setPaginatedProperties(
      filteredProperties.slice(first, first + paginatedPropertiesToShow),
    );
    paginationScrollTo.current.scrollIntoView();
  };

  const { handleInvokeViewItemListEvent } = useViewItemListEvent({
    originCode: airportOriginCode,
    properties: tiles,
    title: campaignPage.headline,
    shouldAutoInvokeOnce: true,
  });

  useEffect(() => {
    handleInvokeViewItemListEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originCode]);

  return (
    <>
      {/* Only /bonus-points page show it */}
      {campaign?.slug == POINTS_EARN_SLUG && (
        <>
          <Box pt={6} bg="background.card">
            <Container>
              <PointAccordion
                headerTitle={
                  <Heading.h5>
                    Book & Earn 10,000 Qantas Points Promotion - how do I earn?
                  </Heading.h5>
                }
              />
            </Container>
          </Box>

          <Box pt="6" bg="background.card">
            <Container>{<QantasFrequentFlyerBanner />}</Container>
          </Box>
        </>
      )}
      <Box py="1" bg="background.card">
        <Container>
          <Banner slug={campaign?.slug} />
          {/* Only /bonus-points page hide it */}
          {campaign?.slug !== POINTS_EARN_SLUG && (
            <ValueProposition px="0" mt="6" mb="1" withTitle />
          )}
          {campaignPage.introCopy && (
            <Box mt="6" mb={['2', null, '8']}>
              <Text>{campaignPage.introCopy}</Text>
            </Box>
          )}
          {!!campaignPage?.tileHeadline && (
            <Box textAlign="start" mt="6">
              <Heading.h3>{campaignPage.tileHeadline}</Heading.h3>
            </Box>
          )}
          <div ref={paginationScrollTo} />
        </Container>
      </Box>

      <FilterBanner>
        <DepartingFrom
          originCodes={originCodes}
          value={originCode}
          onSubmit={handleSetOrigin}
        />
        {destinationNames && destinationNames.length > 1 && (
          <>
            <Box m={2} />
            <TravellingTo
              destinations={destinations}
              destinationNames={destinationNames}
              value={destinationName}
              onSubmit={handleSetDestination}
            />
          </>
        )}
      </FilterBanner>
      <Container>
        <Box mt={6}>
          <Text fontSize="md" textStyle="heading">
            {`Showing ${filteredProperties.length} ${pluralize(
              'package',
              filteredProperties.length,
            )}`}
          </Text>
        </Box>
        <Box mb={['2', null, '8']}>
          <Tiles
            procuredOfferSash={(procuredOfferId) =>
              getProcuredOfferFlightBadgeCode(procuredOfferId, campaigns)
            }
            standardPropertySash={(propertyId) =>
              getStandardPropertyFlightBadgeCode(propertyId, campaigns)
            }
            tiles={paginatedProperties}
            priceForAdult={campaign.priceForAdult}
            hidePerPersonPrice={hidePerPersonPrice}
          />
          <Pagination
            itemsPerPage={paginatedPropertiesToShow}
            onPageChange={updatePaginatedProperties}
            paginatedItem={pluralize('package', filteredProperties.length)}
            totalItems={filteredProperties.length}
          />
        </Box>
        {campaignPage.tileTermsConditions && (
          <Box mb={8}>
            <Text hint>
              <ConditionsMarkdown
                markdown={campaignPage.tileTermsConditions}
                paragraphFontSize="xs"
              />
            </Text>
          </Box>
        )}
      </Container>
      <Flex flexDirection="column" mb="6">
        <Box zIndex="1">
          <Container boxShadow="soft">
            <Box pt={['3', null, '5']} data-testid="DESTINATIONS" mb="2">
              <Destinations
                heading={campaignPage.destinationHeadline}
                destinations={filteredDestinations}
                contentHeight="134px"
              />
            </Box>
          </Container>
        </Box>
      </Flex>
      {campaign.faqs && (
        <Box pb="16">
          <FAQs faqs={campaign.faqs} ga4Category={'campaign'} />
        </Box>
      )}
      <Box bg="background.card">
        <Container pt="10">
          {/* Only /bonus-points page show it */}
          {usePointsEarn() && campaign?.slug == POINTS_EARN_SLUG && (
            <ValueProposition px="0" my="6" mb="1" withTitle />
          )}
          <Box mb={['4', null, '8']}>
            {/* to start a new line, use two spaces and followed by a \n */}
            <Text hint>
              <ConditionsMarkdown
                markdown={campaignPage.longFormTermsConditions}
              />
            </Text>
          </Box>
          <ImportantInformation />
        </Container>
      </Box>
    </>
  );
};

DealsPageContent.propTypes = {
  campaign: PropTypes.shape(Campaign).isRequired,
  destinations: PropTypes.arrayOf(PropTypes.shape(Destination)).isRequired,
  tiles: PropTypes.arrayOf(Tile).isRequired,
};

export default DealsPageContent;
